import { useCallback, useEffect, useState } from 'react';
import API_CONFIG from '../lib/config';
import { formatNaira } from '../lib/data';
import { GiftOrder } from '../types';

export type SubscriptionTierCategory = 'consumer' | 'b2b';

interface SubscriptionPackageResponse {
  _id: string;
  name: string;
  type?: string;
  image_url?: string;
  min_price?: number;
  min_qty?: number;
  description?: string;
  frequency?: string;
  is_popular?: boolean;
  items?: Array<{
    _id?: string;
    qty: number;
    type?: string;
    color?: string;
    size?: string;
    brand?: string;
    item: {
      _id: string;
      name: string;
      price?: number;
      types?: string[];
      colors?: string[];
      brand?: string[] | string;
      sizes?: string[];
    };
  }>;
  vendors?: unknown[];
  delivery_fee?: number;
  createdAt?: string;
  totalPrice?: number;
  category?: string;
  target?: string;
  audience?: string;
}

export interface SubscriptionTier {
  id: string;
  label: string;
  backendCategory?: string;
  imageUrl: string | null;
  priceRange: string;
  period: string;
  includes: string;
  includesList: string[];
  badge: string;
  isPopular: boolean;
  isCustomTier?: boolean;
  ctaLabel?: string;
  minThreshold?: number;
  minQuantity?: number;
  frequency?: string;
  packageTotalPrice?: number;
  packageItems?: NonNullable<GiftOrder['subscription']>['packageItems'];
}

interface UseSubscriptionTiersReturn {
  tiers: SubscriptionTier[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CONSUMER_PACKAGES_ENDPOINT = `${API_CONFIG.SUBSCRIPTION_BASE_URL}/subscriptions/packages?limit=50`;
const B2B_PACKAGES_ENDPOINT = `${API_CONFIG.SUBSCRIPTION_BASE_URL}/subscriptions/packages?type=b2b&limit=50`;

const CATEGORY_COPY: Record<
  SubscriptionTierCategory,
  Pick<SubscriptionTier, 'priceRange' | 'period' | 'includes' | 'includesList' | 'badge'>
> = {
  consumer: {
    priceRange: 'Consumer subscription',
    period: '/ plan',
    includes: 'Discreet essentials subscription curated for reliable household convenience.',
    includesList: [],
    badge: 'Plain bag, no label',
  },
  b2b: {
    priceRange: 'B2B reseller',
    period: '/ plan',
    includes: 'Reliable reseller stock subscription designed for smooth repeat restocking.',
    includesList: [],
    badge: 'Discreet wholesale delivery',
  },
};

const isSubscriptionPackage = (value: unknown): value is SubscriptionPackageResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const tier = value as Record<string, unknown>;
  return typeof tier._id === 'string' && typeof tier.name === 'string';
};

const extractArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const record = payload as Record<string, unknown>;

  if (Array.isArray(record.data)) {
    return record.data;
  }

  if (record.data && typeof record.data === 'object') {
    const nestedData = record.data as Record<string, unknown>;

    if (Array.isArray(nestedData.data)) {
      return nestedData.data;
    }
  }

  if (Array.isArray(record.subscriptions)) {
    return record.subscriptions;
  }

  return [];
};

const inferPackageCategory = (
  subscriptionPackage: SubscriptionPackageResponse
): SubscriptionTierCategory => {
  const haystack = [
    subscriptionPackage.category,
    subscriptionPackage.target,
    subscriptionPackage.audience,
    subscriptionPackage.type,
    subscriptionPackage.name,
    subscriptionPackage.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    haystack.includes('b2b') ||
    haystack.includes('reseller') ||
    haystack.includes('wholesale')
  ) {
    return 'b2b';
  }

  return 'consumer';
};

const buildPackageIncludes = (subscriptionPackage: SubscriptionPackageResponse): string => {
  const packageDescription = subscriptionPackage.description?.trim();
  if (packageDescription) {
    return packageDescription;
  }

  const itemNames =
    subscriptionPackage.items
      ?.map((packageItem) => packageItem.item?.name)
      .filter((name): name is string => Boolean(name))
      .slice(0, 3) ?? [];

  if (itemNames.length > 0) {
    return `Includes ${itemNames.join(', ')}.`;
  }

  return 'Subscription package with curated essentials.';
};

const buildPackageIncludesList = (
  subscriptionPackage: SubscriptionPackageResponse,
  category: SubscriptionTierCategory
): string[] => {
  const listedItems =
    subscriptionPackage.items
      ?.map((packageItem) => {
        const itemName = packageItem.item?.name?.trim();
        if (!itemName) {
          return null;
        }

        if (typeof packageItem.qty === 'number' && packageItem.qty > 0) {
          if (category === 'b2b') {
            return `${packageItem.qty} ${itemName} daily`;
          }

          return `${packageItem.qty} ${itemName}`;
        }

        return itemName;
      })
      .filter((item): item is string => Boolean(item)) ?? [];

  if (listedItems.length > 0) {
    return listedItems;
  }

  return CATEGORY_COPY[category].includesList;
};

const buildPackageBadge = (
  category: SubscriptionTierCategory,
  subscriptionPackage: SubscriptionPackageResponse
): string => {
  if (category === 'b2b') {
    return '';
  }

  const text = `${subscriptionPackage.type ?? ''} ${subscriptionPackage.name}`.toLowerCase();

  if (text.includes('reseller')) {
    return 'Discreet wholesale delivery';
  }

  return CATEGORY_COPY[category].badge;
};

const buildPriceRange = (
  subscriptionPackage: SubscriptionPackageResponse,
  category: SubscriptionTierCategory
): string => {
  if (category === 'b2b') {
    const totalPieces =
      subscriptionPackage.items?.reduce((sum, packageItem) => sum + (packageItem.qty || 0), 0) ?? 0;

    if (totalPieces > 0) {
      return `${totalPieces} pieces`;
    }
  }

  if (typeof subscriptionPackage.totalPrice === 'number' && subscriptionPackage.totalPrice > 0) {
    return formatNaira(subscriptionPackage.totalPrice);
  }

  if (typeof subscriptionPackage.min_price === 'number' && subscriptionPackage.min_price > 0) {
    return `${formatNaira(subscriptionPackage.min_price)} minimum`;
  }

  return CATEGORY_COPY[category].priceRange;
};

const buildPeriod = (
  subscriptionPackage: SubscriptionPackageResponse,
  category: SubscriptionTierCategory
): string => {
  if (
    category === 'b2b' &&
    typeof subscriptionPackage.totalPrice === 'number' &&
    subscriptionPackage.totalPrice > 0
  ) {
    const frequencyLabel = subscriptionPackage.frequency || 'monthly';
    return `${formatNaira(subscriptionPackage.totalPrice)}/${frequencyLabel}`;
  }

  return subscriptionPackage.frequency
    ? `/ ${subscriptionPackage.frequency}`
    : CATEGORY_COPY[category].period;
};

const mapPackageToTier = (
  subscriptionPackage: SubscriptionPackageResponse,
  category: SubscriptionTierCategory
): SubscriptionTier => ({
  id: subscriptionPackage._id,
  label: subscriptionPackage.name,
  backendCategory:
    subscriptionPackage.category?.trim() || subscriptionPackage.type?.trim() || 'general',
  imageUrl: subscriptionPackage.image_url?.trim() ? subscriptionPackage.image_url : null,
  priceRange: buildPriceRange(subscriptionPackage, category),
  period: buildPeriod(subscriptionPackage, category),
  includes: buildPackageIncludes(subscriptionPackage),
  includesList: buildPackageIncludesList(subscriptionPackage, category),
  badge: buildPackageBadge(category, subscriptionPackage),
  isPopular: subscriptionPackage.is_popular === true,
  ctaLabel: category === 'b2b' ? 'Customize This Tier' : undefined,
  minThreshold: subscriptionPackage.min_price,
  minQuantity: subscriptionPackage.min_qty,
  frequency: subscriptionPackage.frequency,
  packageTotalPrice: subscriptionPackage.totalPrice,
  packageItems: subscriptionPackage.items,
});

const buildCustomTier = (category: SubscriptionTierCategory): SubscriptionTier => {
  if (category === 'b2b') {
    return {
      id: 'custom-build-your-own-b2b',
      label: 'Build Your Own',
      backendCategory: 'b2b',
      imageUrl: null,
      priceRange: '5+ pieces minimum',
      period: 'Custom pricing',
      includes: 'Complete flexibility - choose any snacks and quantities',
      includesList: [
        'Choose from all available snacks',
        'Set exact quantities for each item',
        'Customize delivery frequency',
        'Select preferred time slot',
      ],
      badge: '',
      isPopular: false,
      isCustomTier: true,
      ctaLabel: 'Start Building',
      frequency: 'monthly',
      minQuantity: 5,
      packageTotalPrice: 0,
      packageItems: [],
    };
  }

  return {
    id: 'custom-build-your-own',
    label: 'Build Your Own',
    backendCategory: 'general',
    imageUrl: null,
    priceRange: 'Custom',
    period: 'pricing',
    includes: 'Complete flexibility - choose exactly what you need',
    includesList: [
      'Choose your own items',
      'Set your own quantities',
      'Full customization',
    ],
    badge: 'Build from scratch',
    isPopular: false,
    isCustomTier: true,
    ctaLabel: 'Customize Tier',
    frequency: 'monthly',
    minThreshold: 10000,
    packageTotalPrice: 0,
    packageItems: [],
  };
};

export const useSubscriptionTiers = (
  category: SubscriptionTierCategory
): UseSubscriptionTiersReturn => {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTiers = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint =
          category === 'b2b' ? B2B_PACKAGES_ENDPOINT : CONSUMER_PACKAGES_ENDPOINT;

        const response = await fetch(endpoint, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch subscription packages (${response.status})`);
        }

        const payload: unknown = await response.json();
        const rawPackages = extractArray(payload).filter(isSubscriptionPackage);

        if (controller.signal.aborted) {
          return;
        }

        const mappedPackages =
          category === 'b2b'
            ? rawPackages.map((subscriptionPackage) => mapPackageToTier(subscriptionPackage, category))
            : rawPackages
                .map((subscriptionPackage) => ({
                  subscriptionPackage,
                  category: inferPackageCategory(subscriptionPackage),
                }))
                .filter((entry) => entry.category === category)
                .map((entry) => mapPackageToTier(entry.subscriptionPackage, category));

        const nextTiers = [...mappedPackages, buildCustomTier(category)];

        setTiers(nextTiers);

        if (nextTiers.length === 0) {
          setError(`No subscription packages are available for ${category} right now.`);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        const message =
          err instanceof Error ? err.message : 'Unable to load subscription tiers.';
        setError(message);
        setTiers([]);
        console.error('Error fetching subscription tiers:', err);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void fetchTiers();

    return () => controller.abort();
  }, [category, reloadToken]);

  const refetch = useCallback(async () => {
    setReloadToken((current) => current + 1);
  }, []);

  return {
    tiers,
    loading,
    error,
    refetch,
  };
};
